import { useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import PhoneFrame from "./mockups/PhoneFrame";
import HomeMockup from "./mockups/HomeMockup";
import PathMockup from "./mockups/PathMockup";
import LessonMockup from "./mockups/LessonMockup";
import type { Mode } from "./mockups/theme";

const AppPreview = () => {
  const { t } = useLanguage();
  const [mode, setMode] = useState<Mode>("dark");

  const previews = [
    { mockup: <HomeMockup mode={mode} />, titleKey: "preview.1.title", descKey: "preview.1.desc" },
    { mockup: <PathMockup mode={mode} />, titleKey: "preview.2.title", descKey: "preview.2.desc" },
    { mockup: <LessonMockup mode={mode} />, titleKey: "preview.3.title", descKey: "preview.3.desc" },
  ];

  return (
    <section className="py-24 bg-paper">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="eyebrow mb-4" style={{ color: "#6C4DFF" }}>{t("eyebrow.appPreview")}</div>
          <h2 className="font-display text-4xl md:text-5xl text-paper-ink mb-4">
            {t("preview.title").split(" ").slice(0, -1).join(" ")}{" "}
            <em className="text-violet-solid italic">{t("preview.title").split(" ").slice(-1)}</em>
          </h2>
          <p className="text-lg text-paper-ink/60">{t("preview.sub")}</p>
        </div>

        <div className="flex justify-center mb-12">
          <button
            type="button"
            role="switch"
            aria-checked={mode === "dark"}
            aria-label="Toggle mockup theme"
            onClick={() => setMode(mode === "dark" ? "light" : "dark")}
            className={`relative inline-flex items-center h-10 w-[140px] rounded-full p-1 border transition-colors duration-300 ${
              mode === "dark" ? "bg-ink-800 border-ink-600" : "bg-paper-light border-paper-ink/10"
            }`}
          >
            <span className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider z-10 transition-colors ${mode === "light" ? "text-white" : "text-white/70"}`}>
              <Sun className="h-3.5 w-3.5" />Light
            </span>
            <span className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider z-10 transition-colors ${mode === "dark" ? "text-white" : "text-paper-ink/60"}`}>
              <Moon className="h-3.5 w-3.5" />Dark
            </span>
            <span aria-hidden className={`absolute top-1 bottom-1 w-[66px] rounded-full transition-transform duration-300 ease-out ${mode === "dark" ? "translate-x-[66px] bg-violet" : "translate-x-0 bg-violet-solid"}`} />
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-8 max-w-6xl mx-auto place-items-center">
          {previews.map((p, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <PhoneFrame scale={0.78}>{p.mockup}</PhoneFrame>
              <div className="mt-8 max-w-[280px]">
                <h3 className="font-display text-2xl text-paper-ink mb-2">{t(p.titleKey)}</h3>
                <p className="text-sm text-paper-ink/60 leading-relaxed">{t(p.descKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppPreview;
